import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as Sentry from '@sentry/react';

// Prevent duplicate custom element registration errors from third-party scripts
// (Google GSI client and Sentry Replay both register custom elements that can conflict)
const originalDefine = customElements.define.bind(customElements);
customElements.define = function(name: string, constructor: CustomElementConstructor, options?: ElementDefinitionOptions) {
  if (customElements.get(name)) return;
  return originalDefine(name, constructor, options);
};

// Initialize Sentry for error tracking
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || '', // Set via environment variable
  environment: process.env.NODE_ENV,
  enabled: process.env.NODE_ENV === 'production', // Only enable in production
  tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
  replaysSessionSampleRate: 0.1, // 10% of sessions for session replay
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  beforeSend(event, hint) {
    // Filter out non-critical errors or add custom logic
    if (event.exception) {
      const error = hint.originalException;
      // Don't send cancelled requests
      if (error instanceof Error && error.message.includes('cancelled')) {
        return null;
      }
    }
    return event;
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Register service worker for PWA
if (process.env.NODE_ENV === 'production') {
  import('./utils/serviceWorker').then(({ register }) => {
    register({
      onSuccess: () => {
        console.log('Service Worker registered successfully');
      },
      onUpdate: (registration) => {
        console.log('Service Worker updated - new content available');
        // Immediately activate the new service worker so users get the latest code
        const waitingWorker = registration.waiting;
        if (waitingWorker) {
          waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        }
      },
    });
  });
}

// Global ChunkLoadError recovery — handles stale chunk references after new deploys
window.addEventListener('error', (event) => {
  const isChunkError =
    event.message?.includes('ChunkLoadError') ||
    event.message?.includes('Loading chunk') ||
    event.message?.includes('Loading CSS chunk');
  if (isChunkError) {
    const reloadKey = 'chunk-error-reload';
    // Only auto-reload once per session to prevent infinite reload loops
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      console.log('[ChunkLoadError] Clearing caches and reloading...');
      // Clear all caches to remove stale references
      if ('caches' in window) {
        caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n))));
      }
      // Unregister service worker to ensure fresh fetch
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((reg) => reg.unregister());
        });
      }
      // Reload after a tick to allow cache/SW cleanup to start
      setTimeout(() => window.location.reload(), 100);
    }
  }
});

// Clear the chunk-error-reload flag on successful page load so future deploys can auto-recover
window.addEventListener('load', () => {
  sessionStorage.removeItem('chunk-error-reload');
});
